import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import ChannelService, { Channel } from '../../services/channels';

interface ChannelState {
  channels: {
    data: Channel[];
    loading: boolean;
  };
  currentChannel: Channel | null;
  requirements: {
    required_fields: string[];
    optional_fields: string[];
    documentation_url?: string;
  } | null;
  testResult: {
    success: boolean;
    message: string;
    details?: any;
  } | null;
  updating: boolean;
  testing: boolean;
  error: string | null;
}

const initialState: ChannelState = {
  channels: {
    data: [],
    loading: false,
  },
  currentChannel: null,
  requirements: null,
  testResult: null,
  updating: false,
  testing: false,
  error: null,
};

export const fetchChannels = createAsyncThunk(
  'channel/fetchChannels',
  async (_, { rejectWithValue }) => {
    try {
      return await ChannelService.getMerchantChannels();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch channels'
      );
    }
  }
);

export const fetchChannelDetail = createAsyncThunk(
  'channel/fetchChannelDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ChannelService.getChannel(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch channel'
      );
    }
  }
);

export const updateChannelConfig = createAsyncThunk(
  'channel/updateChannelConfig',
  async (
    { id, config }: { id: string; config: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      return await ChannelService.updateChannelConfig(id, config);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update channel config'
      );
    }
  }
);

export const testChannel = createAsyncThunk(
  'channel/testChannel',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ChannelService.testChannel(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to test channel'
      );
    }
  }
);

export const fetchChannelRequirements = createAsyncThunk(
  'channel/fetchChannelRequirements',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ChannelService.getChannelRequirements(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch channel requirements'
      );
    }
  }
);

export const enableChannel = createAsyncThunk(
  'channel/enableChannel',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ChannelService.enableChannel(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to enable channel'
      );
    }
  }
);

export const disableChannel = createAsyncThunk(
  'channel/disableChannel',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ChannelService.disableChannel(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to disable channel'
      );
    }
  }
);

const channelSlice = createSlice({
  name: 'channel',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentChannel: (state) => {
      state.currentChannel = null;
    },
    clearTestResult: (state) => {
      state.testResult = null;
    },
    clearRequirements: (state) => {
      state.requirements = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Channels
    builder
      .addCase(fetchChannels.pending, (state) => {
        state.channels.loading = true;
        state.error = null;
      })
      .addCase(fetchChannels.fulfilled, (state, action: PayloadAction<Channel[]>) => {
        state.channels.loading = false;
        state.channels.data = action.payload;
      })
      .addCase(fetchChannels.rejected, (state, action) => {
        state.channels.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Channel Detail
    builder
      .addCase(fetchChannelDetail.pending, (state) => {
        state.channels.loading = true;
      })
      .addCase(
        fetchChannelDetail.fulfilled,
        (state, action: PayloadAction<Channel>) => {
          state.channels.loading = false;
          state.currentChannel = action.payload;
        }
      )
      .addCase(fetchChannelDetail.rejected, (state, action) => {
        state.channels.loading = false;
        state.error = action.payload as string;
      });

    // Update Channel Config
    builder
      .addCase(updateChannelConfig.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(
        updateChannelConfig.fulfilled,
        (state, action: PayloadAction<Channel>) => {
          state.updating = false;
          // Update in list
          const index = state.channels.data.findIndex(
            (c) => c.id === action.payload.id
          );
          if (index !== -1) {
            state.channels.data[index] = action.payload;
          }
          // Update current if it matches
          if (state.currentChannel?.id === action.payload.id) {
            state.currentChannel = action.payload;
          }
        }
      )
      .addCase(updateChannelConfig.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });

    // Test Channel
    builder
      .addCase(testChannel.pending, (state) => {
        state.testing = true;
        state.error = null;
        state.testResult = null;
      })
      .addCase(
        testChannel.fulfilled,
        (
          state,
          action: PayloadAction<{ success: boolean; message: string; details?: any }>
        ) => {
          state.testing = false;
          state.testResult = action.payload;
        }
      )
      .addCase(testChannel.rejected, (state, action) => {
        state.testing = false;
        state.error = action.payload as string;
        state.testResult = {
          success: false,
          message: action.payload as string,
        };
      });

    // Fetch Channel Requirements
    builder
      .addCase(fetchChannelRequirements.pending, (state) => {
        state.channels.loading = true;
      })
      .addCase(
        fetchChannelRequirements.fulfilled,
        (
          state,
          action: PayloadAction<{
            required_fields: string[];
            optional_fields: string[];
            documentation_url?: string;
          }>
        ) => {
          state.channels.loading = false;
          state.requirements = action.payload;
        }
      )
      .addCase(fetchChannelRequirements.rejected, (state, action) => {
        state.channels.loading = false;
        state.error = action.payload as string;
      });

    // Enable Channel
    builder
      .addCase(enableChannel.pending, (state) => {
        state.updating = true;
      })
      .addCase(enableChannel.fulfilled, (state, action: PayloadAction<Channel>) => {
        state.updating = false;
        // Update in list
        const index = state.channels.data.findIndex(
          (c) => c.id === action.payload.id
        );
        if (index !== -1) {
          state.channels.data[index] = action.payload;
        }
        // Update current if it matches
        if (state.currentChannel?.id === action.payload.id) {
          state.currentChannel = action.payload;
        }
      })
      .addCase(enableChannel.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });

    // Disable Channel
    builder
      .addCase(disableChannel.pending, (state) => {
        state.updating = true;
      })
      .addCase(disableChannel.fulfilled, (state, action: PayloadAction<Channel>) => {
        state.updating = false;
        // Update in list
        const index = state.channels.data.findIndex(
          (c) => c.id === action.payload.id
        );
        if (index !== -1) {
          state.channels.data[index] = action.payload;
        }
        // Update current if it matches
        if (state.currentChannel?.id === action.payload.id) {
          state.currentChannel = action.payload;
        }
      })
      .addCase(disableChannel.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentChannel, clearTestResult, clearRequirements } =
  channelSlice.actions;

export default channelSlice.reducer;
